import { Box, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { BaseModalComponent } from "../../../common/BaseModalComponent";
import type { Job } from "../../interfaces/job.interfaces";
import type { ScopeOfWork } from "../../../scope-of-work/types/scope-of-work.interface";
import { formatJobServiceCostCurrency, type JobServiceCostBreakdown } from "../utils/resolveJobServiceCost";
import { JobServiceCostEdit } from "./JobServiceCostEdit";
import { useQuery } from "@tanstack/react-query";
import { gateway } from "../../../../gateway";
import { useModals } from "src/context/ModalContext";
import { useGetInvoice } from "../../../invoice/view/components/ClientView/hooks/useGetInvoice";
import { InvoiceDetailsView } from "../../../invoice/view/components/DetailsView";
import { RELATED_DEPOSIT_INVOICE_MODAL_KEY } from "../../../invoice/RelatedDepositInvoiceModalHost";

type Props = {
  job: Job;
  scopeOfWork?: ScopeOfWork | null;
  serviceCost: JobServiceCostBreakdown | null;
};

export function JobServiceCostCard({ job, scopeOfWork, serviceCost }: Props) {
  const canEditPricing = Boolean(job.scopeOfWorkUid);
  const { openModal } = useModals();

  const quote = (job as any)?.quote;
  const depositInvoiceUid = quote?.depositInvoiceUid;

  const { data: depositInvoice } = useGetInvoice(depositInvoiceUid || "");

  const { data: usedInvoices = [] } = useQuery<any[]>({
    queryKey: ["invoices", "by-parent-deposit", depositInvoiceUid],
    queryFn: async () => {
      const response = await gateway.get(`/invoices?parentDepositInvoiceUid=${encodeURIComponent(depositInvoiceUid!)}`);
      return response.data?.data ?? [];
    },
    enabled: !!depositInvoiceUid,
  });

  const isDepositPaid = !!(depositInvoice?.paidAt && depositInvoice?.paidWithPaymentMethod);
  const invoicesArray = Array.isArray(usedInvoices) ? usedInvoices : usedInvoices && (usedInvoices as any).uid ? [usedInvoices] : [];
  const isDepositUsed = invoicesArray.length > 0;

  const totalAppliedAmount = invoicesArray.reduce((sum: number, inv: any) => sum + Number(inv.creditedDepositAmount || 0), 0);
  const depositAmount = Number(quote?.depositAmount || depositInvoice?.totalCost?.total?.value || 0);

  const openDepositInvoice = () => {
    if (!depositInvoiceUid) return;
    openModal(
      { key: RELATED_DEPOSIT_INVOICE_MODAL_KEY, preventParentTrigger: true },
      <InvoiceDetailsView invoiceUid={depositInvoiceUid} scopeOfWorkUid={quote?.scopeOfWorkUid} />,
    );
  };

  return (
    <Box
      sx={{
        px: { xs: 2, sm: 2.5 },
        py: 1.5,
        mx: 3,
        borderRadius: 3,
        bgcolor: "background.paper",
      }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1}>
        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em" }}>
          Job service cost
        </Typography>

        {canEditPricing ? (
          <BaseModalComponent
            modalKey={`Job-Edit-Pricing-${job.id}`}
            modalLabel="Edit job pricing"
            defaultSizes={{ width: 640 }}
            elementTrigger={
              <Tooltip title="Edit pricing">
                <IconButton
                  size="small"
                  aria-label="Edit job pricing"
                  sx={{
                    width: 28,
                    height: 28,
                    color: "primary.main",
                    "&:hover": { bgcolor: "rgba(25, 118, 210, 0.08)" },
                  }}
                >
                  <EditOutlinedIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            }
          >
            <JobServiceCostEdit key={`${job.id}-${job.scopeOfWorkUid ?? "no-sow"}`} job={job} scopeOfWork={scopeOfWork} />
          </BaseModalComponent>
        ) : null}
      </Stack>

      {serviceCost ? (
        <>
          <Typography variant="h6" sx={{ fontWeight: 800, mt: 0.5 }}>
            {formatJobServiceCostCurrency(serviceCost.amount, serviceCost.currency)}
          </Typography>

          {serviceCost.showMultiplication && serviceCost.unitPrice != null && serviceCost.units != null ? (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, fontSize: "0.875rem" }}>
              {formatJobServiceCostCurrency(serviceCost.unitPrice, serviceCost.currency)} × {serviceCost.units} {serviceCost.unitLabel}
            </Typography>
          ) : null}

          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25, fontSize: "0.8125rem" }}>
            Source: {serviceCost.source}
          </Typography>
        </>
      ) : (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75, fontSize: "0.8125rem" }}>
          Job service cost is not available (no linked quote total, override, or priced areas in this scope).
        </Typography>
      )}

      {depositInvoiceUid && !isDepositUsed && (
        <Box
          sx={{
            mt: 1.5,
            pt: 1.5,
            borderTop: "1px solid",
            borderColor: "divider",
          }}
        >
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ fontWeight: 700, display: "block", mb: 0.75, textTransform: "uppercase", letterSpacing: "0.05em" }}
          >
            Deposit status
          </Typography>

          <Stack direction="row" alignItems="center" gap={1}>
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                bgcolor: isDepositPaid ? "success.main" : "warning.main",
              }}
            />
            <Typography variant="body2" sx={{ fontWeight: 600, color: "text.primary" }}>
              {isDepositPaid ? "Paid" : "Unpaid"}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              •
            </Typography>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 600,
                color: isDepositUsed ? "success.main" : "info.main",
              }}
            >
              {isDepositUsed
                ? `Applied for first invoice (${formatJobServiceCostCurrency(totalAppliedAmount, depositInvoice?.totalCost?.total?.currency || "USD")})`
                : `Available/Unused (${formatJobServiceCostCurrency(depositAmount, depositInvoice?.totalCost?.total?.currency || "USD")})`}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              •
            </Typography>
            <Typography
              variant="body2"
              onClick={openDepositInvoice}
              sx={{
                fontWeight: 600,
                color: "primary.main",
                cursor: "pointer",
                textDecoration: "underline",
                "&:hover": { color: "primary.dark" },
              }}
            >
              View Deposit Invoice
            </Typography>
          </Stack>
        </Box>
      )}
    </Box>
  );
}
